import React, { useCallback, useEffect, useRef, useState } from "react";
import { throttle } from "./throttle";
import { HighlightConfirmationPopup } from "./highlight-confirmation-popup";
import { getUniqueXpath } from "./selectorBuilder";

const OMIT_IDS = ["pyne-selector-picker", "pyne-debugger", "pyne-selector-picker-badge"];

function useHighlighter({ isSelecting, onPick, onScreenshot }: HighlighterProps) {
  const [styles, setStyles] = useState<Record<string, number>>();
  const [selector, setSelector] = useState<{ selector: string; rank: number }>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isElementSelected, setIsElementSelected] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [showCaptured, setShowCaptured] = useState(false);
  const prevDOM = useRef<HTMLElement | null>(null);
  const targetElement = useRef<HTMLElement | null>(null);
  const targetSelector = useRef<{ selector: string; rank: number } | null>(null);
  const [canNavigateUp, setCanNavigateUp] = useState(false);
  const [canNavigateDown, setCanNavigateDown] = useState(false);
  const [auxiliaryHighlightStyles, setAuxiliaryHighlightStyles] = useState<{
    parent?: React.CSSProperties;
    child?: React.CSSProperties;
  }>({});
  const [popupPlacement, setPopupPlacement] = useState<"top" | "bottom">("top");

  const updateTargetElement = useCallback((element: HTMLElement | null) => {
    if (!element) return;

    prevDOM.current = element;
    targetElement.current = element;

    const { top, left, width, height } = element.getBoundingClientRect();
    const { borderRadius } = getComputedStyle(element);

    setStyles({
      top,
      left,
      width,
      height,
      borderRadius: Number(borderRadius.replace("px", "")),
    });

    const { selector, rank } = getUniqueXpath(element) || { selector: "", rank: 0 };
    setSelector({ selector, rank });
    targetSelector.current = { selector, rank };

    // Determine popup placement based on where badges will be rendered
    const badgesWillBeAboveElement = top >= 50; // Mirrors the logic for isElementAtTop in Highlighter
    if (badgesWillBeAboveElement) {
      setPopupPlacement("bottom");
    } else {
      setPopupPlacement("top");
    }

    const navUp = !!element.parentElement;
    const navDown = !!(element.firstElementChild && element.firstElementChild instanceof HTMLElement);

    setCanNavigateUp(navUp);
    setCanNavigateDown(navDown);

    const newAuxStyles: typeof auxiliaryHighlightStyles = {};
    if (navUp && element.parentElement) {
      const rect = element.parentElement.getBoundingClientRect();
      const br = getComputedStyle(element.parentElement).borderRadius;
      newAuxStyles.parent = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        borderRadius: Number(br.replace("px", "")),
      };
    }
    if (navDown && element.firstElementChild) {
      const el = element.firstElementChild as HTMLElement;
      const rect = el.getBoundingClientRect();
      const br = getComputedStyle(el).borderRadius;
      newAuxStyles.child = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        borderRadius: Number(br.replace("px", "")),
      };
    }
    setAuxiliaryHighlightStyles(newAuxStyles);
  }, []);

  const reset = useCallback(() => {
    setStyles(undefined);
    setSelector(undefined);
    setShowConfirmation(false);
    setIsElementSelected(false);
    setIsFadingOut(false);
    setShowCaptured(false);
    prevDOM.current = null;
    targetElement.current = null;
    targetSelector.current = null;
    setCanNavigateUp(false);
    setCanNavigateDown(false);
    setAuxiliaryHighlightStyles({});
    setPopupPlacement("top"); // Reset placement
  }, []);

  const [updateHighlight] = throttle(({ target }: Event) => {
    // Don't update highlight if we have a selected element
    if (isElementSelected) return;

    if (!isSelecting || !(target instanceof HTMLElement) || target === prevDOM.current) return;

    // Skip the blocker element
    if (target instanceof HTMLElement && target.id === "pyne-click-blocker") return;

    // Skip elements that are descendants of PYNE_RECORDER_ROOT_ID
    let current: HTMLElement | null = target as HTMLElement;
    while (current) {
      if (OMIT_IDS.includes(current.id)) return;
      current = current.parentElement;
    }

    prevDOM.current = target;
    targetElement.current = target; // Keep targetElement in sync

    const { top, left, width, height } = target.getBoundingClientRect();
    const { borderRadius } = getComputedStyle(target);

    setStyles({
      top,
      left,
      width,
      height,
      borderRadius: Number(borderRadius.replace("px", "")),
    });

    const { selector, rank } = getUniqueXpath(target) || "";
    setSelector({ selector, rank });

    // Store the current target and selector for later use
    targetSelector.current = { selector, rank }; // Storing selector object

    // Update navigation availability
    setCanNavigateUp(!!target.parentElement);
    setCanNavigateDown(!!(target.firstElementChild && target.firstElementChild instanceof HTMLElement));

    const newAuxStyles: typeof auxiliaryHighlightStyles = {};
    if (target.parentElement) {
      const rect = target.parentElement.getBoundingClientRect();
      const br = getComputedStyle(target.parentElement).borderRadius;
      newAuxStyles.parent = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        borderRadius: Number(br.replace("px", "")),
      };
    }
    if (target.firstElementChild && target.firstElementChild instanceof HTMLElement) {
      const el = target.firstElementChild as HTMLElement;
      const rect = el.getBoundingClientRect();
      const br = getComputedStyle(el).borderRadius;
      newAuxStyles.child = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        borderRadius: Number(br.replace("px", "")),
      };
    }
    setAuxiliaryHighlightStyles(newAuxStyles);

    // Removed popupPlacement logic from here to avoid jumpiness on hover
  }, 150);

  const [updateHighlightPosition] = throttle(() => {
    if (!isSelecting || !prevDOM.current) return;

    const mainTarget = prevDOM.current; // This is the element under the mouse
    const { top, left, width, height } = mainTarget.getBoundingClientRect();
    const { borderRadius } = getComputedStyle(mainTarget);

    setStyles({
      top,
      left,
      width,
      height,
      borderRadius: Number(borderRadius.replace("px", "")),
    });

    // If an element is actually selected (confirmation popup is potentially visible or active)
    // update auxiliary highlights based on that selected element, not the one under the mouse.
    if (targetElement.current) {
      const currentSelectedElement = targetElement.current;
      const newAuxStyles: typeof auxiliaryHighlightStyles = {};

      if (currentSelectedElement.parentElement) {
        const rect = currentSelectedElement.parentElement.getBoundingClientRect();
        const br = getComputedStyle(currentSelectedElement.parentElement).borderRadius;
        newAuxStyles.parent = {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          borderRadius: Number(br.replace("px", "")),
        };
      }
      if (currentSelectedElement.firstElementChild && currentSelectedElement.firstElementChild instanceof HTMLElement) {
        const el = currentSelectedElement.firstElementChild as HTMLElement;
        const rect = el.getBoundingClientRect();
        const br = getComputedStyle(el).borderRadius;
        newAuxStyles.child = {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          borderRadius: Number(br.replace("px", "")),
        };
      }
      setAuxiliaryHighlightStyles(newAuxStyles);
    }
  }, 300);

  const clickHandler = useCallback(
    async (e: MouseEvent) => {
      // Check if target is part of the PYNE_RECORDER_ROOT_ID
      let current: HTMLElement | null = e.target as HTMLElement;
      while (current) {
        if (OMIT_IDS.includes(current.id)) {
          // Allow clicks to pass through for elements within PYNE_RECORDER_ROOT_ID
          return true;
        }
        current = current.parentElement;
      }

      if (isSelecting) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Remove the overlay if it exists
        const blocker = document.getElementById("pyne-click-blocker");
        if (blocker) {
          document.body.removeChild(blocker);
        }

        // Use the stored selector, not the one from the blocker
        const finalSelector = targetSelector.current;

        if (!finalSelector) return false;

        document.removeEventListener("mousemove", updateHighlight);
        window.removeEventListener("scroll", updateHighlightPosition, true);
        document.removeEventListener("click", clickHandler, { capture: true });
        document.removeEventListener("mousedown", mouseDownHandler, { capture: true });

        // Ensure all states are updated based on the definitively selected element
        if (targetElement.current) {
          updateTargetElement(targetElement.current);
        }

        // Mark element as selected and show confirmation popup
        setIsElementSelected(true);
        setShowConfirmation(true);

        return false;
      }
    },
    [isSelecting, updateHighlight, updateHighlightPosition, onScreenshot, updateTargetElement]
  );

  const handleSave = useCallback(async () => {
    setIsFadingOut(true);
    setShowConfirmation(false);

    if (onScreenshot) {
      onScreenshot();
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setShowCaptured(true);
    setTimeout(() => {
      setShowCaptured(false);
    }, 500);
    setTimeout(() => {
      if (targetSelector.current) {
        onPick(targetSelector.current);
      }

      reset();
    }, 1000);
  }, [onPick, reset]);

  const handleCancel = useCallback(() => {
    reset();
  }, [reset]);

  const handleNavigateUp = useCallback(() => {
    if (targetElement.current && targetElement.current.parentElement) {
      updateTargetElement(targetElement.current.parentElement);
    }
  }, [updateTargetElement]);

  const handleNavigateDown = useCallback(() => {
    if (targetElement.current && targetElement.current.firstElementChild instanceof HTMLElement) {
      updateTargetElement(targetElement.current.firstElementChild as HTMLElement);
    }
  }, [updateTargetElement]);

  const mouseDownHandler = useCallback(
    (e: MouseEvent) => {
      // Check if target is part of the PYNE_RECORDER_ROOT_ID
      let current: HTMLElement | null = e.target as HTMLElement;
      while (current) {
        if (OMIT_IDS.includes(current.id)) {
          // Allow events to pass through for elements within PYNE_RECORDER_ROOT_ID
          return true;
        }
        current = current.parentElement;
      }

      if (isSelecting) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Make sure we capture the selector before creating the overlay
        if (!targetSelector.current && targetElement.current) {
          targetSelector.current = getUniqueXpath(targetElement.current);
        }

        // Create invisible overlay div with transparent background
        const overlay = document.createElement("div");
        overlay.id = "pyne-click-blocker";
        overlay.setAttribute("data-pyne-ignore", "true");
        overlay.style.position = "fixed";
        overlay.style.top = "0";
        overlay.style.left = "0";
        overlay.style.width = "100vw";
        overlay.style.height = "100vh";
        overlay.style.zIndex = "2147483646";
        overlay.style.backgroundColor = "transparent";

        // Add a direct click handler to the overlay
        overlay.addEventListener(
          "click",
          (evt) => {
            evt.preventDefault();
            evt.stopPropagation();

            // Trigger our click handler directly
            clickHandler(evt as unknown as MouseEvent);

            return false;
          },
          true
        );

        document.body.appendChild(overlay);

        return false;
      }
    },
    [isSelecting, clickHandler]
  );

  const addEventListeners = useCallback(() => {
    document.addEventListener("mousemove", updateHighlight);
    window.addEventListener("scroll", updateHighlightPosition, true);
    document.addEventListener("click", clickHandler, { capture: true });
    document.addEventListener("mousedown", mouseDownHandler, { capture: true });
  }, [clickHandler, mouseDownHandler, updateHighlight, updateHighlightPosition]);

  const removeEventListeners = useCallback(() => {
    document.removeEventListener("mousemove", updateHighlight);
    window.removeEventListener("scroll", updateHighlightPosition, true);
    document.removeEventListener("click", clickHandler, { capture: true });
    document.removeEventListener("mousedown", mouseDownHandler, { capture: true });
  }, [clickHandler, mouseDownHandler, updateHighlight, updateHighlightPosition]);

  useEffect(() => {
    addEventListeners();

    return () => removeEventListeners();
  }, [addEventListeners, removeEventListeners]);

  useEffect(() => {
    if (!showConfirmation) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowUp") {
        event.preventDefault();
        handleNavigateUp();
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        handleNavigateDown();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showConfirmation, handleNavigateUp, handleNavigateDown]);

  return {
    styles,
    selector,
    reset,
    showConfirmation,
    targetElement: targetElement.current,
    handleSave,
    handleCancel,
    handleNavigateUp,
    handleNavigateDown,
    isElementSelected,
    isFadingOut,
    showCaptured,
    canNavigateUp,
    canNavigateDown,
    auxiliaryHighlightStyles,
    popupPlacement,
  };
}

export type HighlighterProps = {
  isSelecting: boolean;
  onPick: (selector: { selector: string; rank: number }) => void;
  onReset?: () => void;
  onScreenshot?: () => void;
};

export default function Highlighter({ isSelecting, onPick, onScreenshot }: HighlighterProps) {
  const {
    styles,
    selector,
    reset,
    showConfirmation,
    targetElement,
    handleSave,
    handleCancel,
    handleNavigateUp,
    handleNavigateDown,
    isElementSelected,
    isFadingOut,
    showCaptured,
    canNavigateUp,
    canNavigateDown,
    auxiliaryHighlightStyles,
    popupPlacement,
  } = useHighlighter({ isSelecting, onPick, onScreenshot });

  useEffect(() => {
    if (!isSelecting) {
      reset();
    }
  }, [isSelecting, reset]);

  useEffect(() => {
    // Add keyframe animation
    const style = document.createElement("style");
    style.textContent = `
      @keyframes fadeInOut {
        0% { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
        10% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        90% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        100% { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (!styles) return null;

  const hasSelected = selector && !isSelecting;

  const baseHighlighterStyles: React.CSSProperties = {
    pointerEvents: "none",
    position: "fixed",
    zIndex: 2147483647,
    display: "block",
    cursor: "crosshair",
    userSelect: "none",
    borderWidth: "2px",
    borderStyle: "solid",
    borderColor: "#9E9E9E",
    backgroundColor: "rgba(158, 158, 158, 0.4)",
  };

  const highlighterStyle: React.CSSProperties = {
    ...styles,
    ...baseHighlighterStyles,
    opacity: isFadingOut ? 0 : 1,
    transition: "opacity 1s ease-out, background-color 0.3s ease",
  };

  if (isElementSelected && !isFadingOut) {
    highlighterStyle.backgroundColor = "rgba(255, 215, 0, 0.5)";
    highlighterStyle.borderColor = "#FFD700";
  }
  if (isFadingOut) {
    highlighterStyle.backgroundColor = "rgba(76, 175, 80, 0.4)";
    highlighterStyle.borderColor = "#4CAF50";
  }
  if (hasSelected || isElementSelected) {
    highlighterStyle.borderColor = "orange";
    highlighterStyle.borderWidth = "3px";
  }

  const isElementAtTop = styles.top < 70; // Threshold for top position

  const auxiliaryHighlighterBaseStyle: React.CSSProperties = {
    pointerEvents: "none",
    position: "fixed",
    zIndex: 2147483645, // Base for parent, below child aux and main highlight
    display: "block",
    userSelect: "none",
    borderWidth: "2px",
    borderStyle: "dashed",
    borderColor: "#808080", // Gray
    backgroundColor: "rgba(128, 128, 128, 0.1)", // Light gray, slightly transparent
  };

  const baseArrowIconStyle: React.CSSProperties = {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    // color will be set by fill attribute on SVG
    width: "20px",
    height: "20px",
  };

  const arrowIconParentStyle: React.CSSProperties = { ...baseArrowIconStyle, top: "50%", left: "50%" }; // Shifted up
  const arrowIconChildStyle: React.CSSProperties = { ...baseArrowIconStyle, top: "50%", left: "50%" }; // Shifted down

  return (
    <>
      {showCaptured && (
        <div
          style={{
            position: "fixed",
            zIndex: 2147483647,
            transform: "translate(-50%, -50%)",
            borderRadius: "0.5rem",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            paddingTop: "0.5rem",
            paddingBottom: "0.5rem",
            paddingLeft: "1rem",
            paddingRight: "1rem",
            color: "white",
            animation: "fadeInOut 500ms ease-in-out forwards",
            left: styles.left + styles.width / 2,
            top: styles.top + styles.height / 2,
          }}
        >
          Captured!
        </div>
      )}
      {isSelecting && !isFadingOut && (
        <span
          id="pyne-selector"
          style={{
            position: "fixed",
            zIndex: 2147483647,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            borderRadius: "0.375rem",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            paddingLeft: "0.5rem",
            paddingRight: "0.5rem",
            paddingTop: "2px",
            fontSize: "0.75rem",
            lineHeight: "1.75rem",
            color: "#D3D3D3",
            left: styles.left,
            opacity: selector ? 1 : 0,
            top: isElementAtTop ? styles.top + styles.height + 5 : styles.top - 35, // Adjusted top
            transition: "opacity 1s ease-out",
          }}
        >
          {selector?.selector}
        </span>
      )}
      {isSelecting && !isFadingOut && selector && selector.rank && (
        <div
          style={{
            position: "fixed",
            zIndex: 2147483646,
            borderRadius: "0.375rem",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            padding: "0.25rem 0.5rem",
            fontSize: "0.75rem",
            color: "#D3D3D3",
            left: styles.left,
            top: isElementAtTop ? styles.top + styles.height + 40 : styles.top - 65, // Adjusted top
            display: "flex",
            alignItems: "center",
          }}
        >
          <span
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: selector.rank <= 2 ? "lightgreen" : selector.rank <= 4 ? "yellow" : "red",
              marginRight: "0.5rem",
            }}
          />
          Selector quality: {selector.rank <= 2 ? "good" : selector.rank <= 4 ? "moderate" : "bad"}
        </div>
      )}
      <div style={highlighterStyle} />
      <HighlightConfirmationPopup
        isOpen={showConfirmation}
        onSave={handleSave}
        onCancel={handleCancel}
        onNavigateUp={handleNavigateUp}
        onNavigateDown={handleNavigateDown}
        referenceElement={targetElement}
        canNavigateUp={canNavigateUp}
        canNavigateDown={canNavigateDown}
        popupPlacement={popupPlacement}
      />
      {showConfirmation && auxiliaryHighlightStyles.parent && (
        <div
          style={{
            ...auxiliaryHighlighterBaseStyle,
            ...auxiliaryHighlightStyles.parent,
            backgroundColor: "rgba(173, 216, 230, 0.2)", // Light blue
            borderColor: "#ADD8E6", // Keeping border light blue
            // zIndex will be from auxiliaryHighlighterBaseStyle (2147483645)
          }}
        >
          <svg
            style={arrowIconParentStyle}
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="#4682B4"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
            />
          </svg>
        </div>
      )}
      {showConfirmation && auxiliaryHighlightStyles.child && (
        <div
          style={{
            ...auxiliaryHighlighterBaseStyle,
            ...auxiliaryHighlightStyles.child,
            backgroundColor: "rgba(144, 238, 144, 0.2)", // Light green
            borderColor: "#90EE90", // Keeping border light green
            zIndex: 2147483647, // Child aux highlight above parent aux
          }}
        >
          <svg
            style={arrowIconChildStyle}
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="#2E8B57"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
            />
          </svg>
        </div>
      )}
    </>
  );
}
